<template>
  <div class="item-row">
    <div class="f fs gap20">
      <NuxtLink
        :aria-label="`Link to skin${item.market_hash_name}`"
        :to="localePath(getItemLinkWithExterior({
          name: volumeItem?.market_hash_name || item.market_hash_name,
          slug: volumeItem?.link || item.link,
        }))"
        class=""
      >
        <img
          :alt="item.market_hash_name"
          :src="
            getImageFromWaxpeer(
              item.link?.includes('sunset-storm')
                ? `${item.market_hash_name}-${item.exterior}`
                : `${item.link || `${item.market_hash_name}-${item.exterior}`}`,
              '100:100',
            )
          "
          class="item-row__img"
        >
      </NuxtLink>
      <div class="item-row__info">
        <div class="item-row__info__top">
          <div
            :style="{ background: item.rarity_color }"
            class="rarity-dot"
          />
          <span
            class="title"
            @click="copyName(item.market_hash_name)"
          >
            {{ getItemNameWithCategory(item.locals?.localed_name) }}
            ({{
              volumeItem
                ? getExterior(volumeItem.full_name)
                : getExteriorByFloat(volumeItem?.float || item.float)
            }})
          </span>
        </div>
        <p class="item-row__info__bottom">
          {{ item.collection }}
        </p>
      </div>
    </div>
    <div class="item-row__right">
      <div class="input-wrapper">
        <ui-input-label-inside
          v-model="item.float"
          :clearable="false"
          :label="t('additional.float')"
          class="right-input"
          placeholder="0"
          type="number"
        />
        <p
          v-if="
            item.float > item.item_max_float || item.float < item.item_min_float
          "
          class="input-error red f12"
        >
          {{
            t("float_error", {
              min: item.item_min_float,
              max: item.item_max_float,
            })
          }}
        </p>
      </div>
      <div class="price-wrapper">
        <template v-if="volumeItem">
          <img
            :src="
              getImageFromProxy(
                `${getMarketImage(volumeItem.market)}.png`,
                '50:50',
              )
            "
            alt=""
          >
          <UiPrice :price="volumeItem?.min_price || 0" />
        </template>
        <div
          v-else
          class="f fc w-100"
        >
          <i class="i-loading" />
        </div>
      </div>

      <button
        class="btn btn-clear red"
        type="button"
        @click="emit('removeItem')"
      >
        <svg
          height="20"
          width="20"
        >
          <use xlink:href="/icons/sprite.svg#i-trash-outline" />
        </svg>
      </button>
    </div>
    <!-- {{ volumeItem }} -->
  </div>
</template>

<script lang="ts" setup>
import type { TradeUpSelectedItem } from '~/components/Pages/TradeUp/types';
import { EventBus } from '~/composables/bus';
import { getExterior, getItemLinkWithExterior, getModifiedString } from '~/utils/name-utils';

const emit = defineEmits(['removeItem']);
const localePath = useLocalePath();
const item = defineModel<TradeUpSelectedItem>('item', {
  type: Object,
  default: {} as TradeUpSelectedItem,
});

const { t } = useI18n();

function copyName(name: string) {
  navigator.clipboard.writeText(name);

  EventBus.$emit('notify', {
    success: true,
    msg: t('notify.copied', { name }),
  });
}
const volumeItem = computed(() => {
  const finded = item.value.volume?.find((i) => {
    return (
      i?.link
      === getModifiedString(
        `${getItemNameWithCategory(item.value.market_hash_name)}-${getExteriorByFloat(item.value.float)}`,
      )
    );
  });
  if (finded) {
    return finded;
  } else {
    let closest;
    if (item.value.float > item.value.item_min_float)
      closest = item.value.volume[item.value.volume.length - 1];
    else
      closest = item.value.volume[0];
    return closest;
  }
});
// const getItemLink = (name: string): string => {
//   if (!name) return '';
//
//   return name
//       .replace(/[^a-z\d]+/gi, '-')
//       .replace(/^-+|-+$/g, '')
//       .toLowerCase()
// };
</script>

<style lang="scss" scoped>
.item-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  border-radius: 8px;
  background: var(--surfaces-highest-container);
  padding: 12px;

  &__img {
    height: 40px;
    object-fit: contain;
  }

  &__info {
    &__top {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;

      .rarity-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }

      .title {
        color: #fff;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      }
    }

    &__bottom {
      color: var(--accent-on-surface);
      font-size: 12px;
      font-weight: 400;
      line-height: 20px; /* 166.667% */
    }
  }

  &__right {
    display: grid;
    grid-template-columns: 4fr 2fr 1fr;
    gap: 12px;
    align-items: center;
    max-width: 290px;
    margin-left: auto;

    &:deep(.form_label) {
      top: -9px !important;
    }

    &:deep(.right-input) {
      border-radius: 6px;
      border: 1px solid var(--surfaces-outline) !important;
      background: var(--surfaces-high-container);
      height: 32px;
      color: var(--surfaces-on-surface-variable);
      font-size: 14px;
      font-weight: 400;
    }

    .price-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;

      img {
        width: 16px;
        height: 16px;
      }

      &:deep(.price) {
        color: var(--surfaces-on-surface);
        font-size: 14px;
        font-weight: 600;
      }
    }

    .btn-clear {
      padding: 8px 5px;

      &:hover {
        background: var(--surfaces-high-container);
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .item-row {
    grid-template-columns: 1fr;

    &__right {
      max-width: 100%;
      margin-left: 0;
    }
  }
}
</style>
